import React, { useState, useEffect } from "react";
import { Table, Modal, Spinner } from "react-bootstrap";
import { Paper } from "@material-ui/core";
import { useAlert } from "react-alert";
import { baseUrl } from "../../../../services/config";

const STATUS_COLORS = {
  present: { bg: "#d4edda", color: "#155724", label: "Present" },
  late_coming: { bg: "#fff3cd", color: "#856404", label: "Late Coming" },
  missing: { bg: "#f8d7da", color: "#721c24", label: "Missing" },
  absent: { bg: "#f5c6cb", color: "#721c24", label: "Absent" },
  off_day: { bg: "#e2e3e5", color: "#383d41", label: "Off Day" },
  manual_leave: { bg: "#cce5ff", color: "#004085", label: "Leave" },
  casual_leave: { bg: "#cce5ff", color: "#004085", label: "Casual Leave" },
  sick_leave: { bg: "#d1ecf1", color: "#0c5460", label: "Sick Leave" },
  short_leave: { bg: "#e2e3e5", color: "#383d41", label: "Short Leave" },
  annual_leave: { bg: "#d1ecf1", color: "#0c5460", label: "Annual Leave" },
  sandwich_absent: { bg: "#f5c6cb", color: "#721c24", label: "Sandwich Absent" },
  leave_attachment_absent: { bg: "#f5c6cb", color: "#721c24", label: "Leave Attach Absent" },
};

const MANUAL_STATUS_OPTIONS = [
  { value: 1, label: "Present" },
  { value: 2, label: "Absent" },
  { value: 3, label: "Casual Leave" },
  { value: 4, label: "Sick Leave" },
  { value: 5, label: "Short Leave" },
  { value: 6, label: "Annual Leave" },
];

const StatusBadge = ({ status }) => {
  const cfg = STATUS_COLORS[status] || { bg: "#e2e3e5", color: "#383d41", label: status };
  return (
    <span
      style={{
        background: cfg.bg,
        color: cfg.color,
        padding: "3px 10px",
        borderRadius: 12,
        fontSize: 11,
        fontWeight: 600,
        whiteSpace: "nowrap",
      }}
    >
      {cfg.label}
    </span>
  );
};

const AttendanceLogs = () => {
  const authtoken = localStorage.getItem("userToken");
  const alert = useAlert();

  const today = new Date();
  const defaultMonth = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}`;

  const [empCode, setEmpCode] = useState("");
  const [salaryMonth, setSalaryMonth] = useState(defaultMonth);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [logsData, setLogsData] = useState(null);
  const [showRawLogs, setShowRawLogs] = useState(false);

  const [showManualModal, setShowManualModal] = useState(false);
  const [manualDate, setManualDate] = useState("");
  const [manualStatus, setManualStatus] = useState(1);
  const [savingManual, setSavingManual] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState("");

  useEffect(() => {
    fetchSessions();
  }, []);

  const fetchSessions = async () => {
    try {
      const res = await fetch(`${baseUrl}/session`, {
        headers: { Authorization: `Bearer ${authtoken}` },
      });
      const data = await res.json();
      if (data.metadata?.success) {
        setSessions(data.payload);
        const active = data.payload.find((s) => s.active_financial_year === 1);
        if (active) setSelectedSession(active.id);
      }
    } catch (err) {
      console.log(err);
    }
  };

  const fetchLogs = async () => {
    const code = empCode.trim();
    if (!code) {
      alert.show("Please enter an employee code.");
      return;
    }
    setLoadingLogs(true);
    setLogsData(null);
    setShowRawLogs(false);
    try {
      const params = new URLSearchParams({
        emp_code: code,
        salary_month: salaryMonth + "-01",
      });
      const res = await fetch(
        `${baseUrl}/employees/biometric-attendance-logs/by-code?${params}`,
        { headers: { Authorization: `Bearer ${authtoken}` } }
      );
      const data = await res.json();
      if (data.metadata?.success) {
        setLogsData(data.payload);
      } else {
        const msg = data.metadata?.message;
        const errorText =
          msg && typeof msg === "object"
            ? Object.values(msg).flat().join(" ")
            : typeof msg === "string"
            ? msg
            : "No data found for this employee code.";
        alert.error(errorText);
      }
    } catch (err) {
      console.log(err);
      alert.error("Failed to fetch logs.");
    } finally {
      setLoadingLogs(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") fetchLogs();
  };

  const openManualModal = (dateStr) => {
    setManualDate(dateStr);
    setManualStatus(1);
    setShowManualModal(true);
  };

  const saveManualAttendance = async () => {
    const employeeId = logsData?.employee?.id;
    if (!employeeId) {
      alert.error("Employee ID not found.");
      return;
    }
    setSavingManual(true);
    try {
      const payload = {
        employee_id: employeeId,
        attendance_date: manualDate,
        status: manualStatus,
      };
      if (selectedSession) payload.session_id = selectedSession;

      const res = await fetch(`${baseUrl}/manual-attendances`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authtoken}`,
        },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (data.metadata?.success) {
        alert.show("Manual attendance saved successfully.");
        setShowManualModal(false);
        fetchLogs();
      } else {
        const msg = data.metadata?.message;
        alert.error(typeof msg === "string" ? msg : "Failed to save attendance.");
      }
    } catch (err) {
      console.log(err);
      alert.error("Something went wrong.");
    } finally {
      setSavingManual(false);
    }
  };

  return (
    <Paper style={{ padding: 20, marginTop: 10 }}>
      <h4 className="mb-4">Biometric Logs</h4>

      {/* Search bar */}
      <div className="row mb-3 align-items-end">
        <div className="col-md-3">
          <label className="form-control-label"><b>Employee Code</b></label>
          <input
            className="form-control"
            placeholder="e.g. 1023"
            value={empCode}
            onChange={(e) => { setEmpCode(e.target.value); setLogsData(null); }}
            onKeyDown={handleKeyDown}
          />
        </div>

        <div className="col-md-2">
          <label className="form-control-label"><b>Month</b></label>
          <input
            type="month"
            className="form-control"
            value={salaryMonth}
            onChange={(e) => { setSalaryMonth(e.target.value); setLogsData(null); }}
          />
        </div>

        <div className="col-md-2">
          <button
            onClick={fetchLogs}
            disabled={loadingLogs}
            className="btn btn-success button_blue w-100"
          >
            {loadingLogs ? <Spinner size="sm" animation="border" /> : "Fetch Logs"}
          </button>
        </div>
      </div>

      {/* Employee info strip */}
      {logsData && (
        <div
          style={{
            background: "#f8f9fa",
            borderRadius: 6,
            padding: "10px 16px",
            marginBottom: 16,
            display: "flex",
            gap: 24,
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          <span><b>Employee:</b> {logsData.employee?.name}</span>
          <span><b>Code:</b> {logsData.employee?.emp_code}</span>
          <span>
            <b>Device ID:</b>{" "}
            {logsData.employee?.biometric_device_user_id || <i className="text-muted">Not linked</i>}
          </span>
          <span><b>Month:</b> {logsData.salary_month}</span>
          {logsData.message && (
            <span
              style={{
                color: "#856404",
                background: "#fff3cd",
                padding: "2px 10px",
                borderRadius: 4,
                fontSize: 12,
              }}
            >
              ⚠ {logsData.message}
            </span>
          )}
        </div>
      )}

      {/* Daily Details Table */}
      {loadingLogs ? (
        <div className="text-center my-5">
          <Spinner animation="border" variant="primary" />
        </div>
      ) : logsData ? (
        <>
          <Table responsive bordered size="sm">
            <thead className="tablehead">
              <tr className="tableheadtr text-center">
                <th className="background-color-primary tableHEadNAme">Date</th>
                <th className="background-color-primary tableHEadNAme">Day</th>
                <th className="background-color-primary tableHEadNAme">Status</th>
                <th className="background-color-primary tableHEadNAme">Source</th>
                <th className="background-color-primary tableHEadNAme">Manual Status</th>
                <th className="background-color-primary tableHEadNAme">First In</th>
                <th className="background-color-primary tableHEadNAme">Last Out</th>
                <th className="background-color-primary tableHEadNAme">Note</th>
                <th className="background-color-primary tableHEadNAme">Mark</th>
              </tr>
            </thead>
            <tbody>
              {!logsData.daily_details?.length ? (
                <tr>
                  <td colSpan={9} className="text-center text-muted py-4">
                    No attendance data available.
                  </td>
                </tr>
              ) : (
                logsData.daily_details.map((day) => (
                  <tr key={day.date}>
                    <td style={{ whiteSpace: "nowrap", fontWeight: 500 }}>{day.date}</td>
                    <td>{day.day_name}</td>
                    <td><StatusBadge status={day.final_status} /></td>
                    <td style={{ fontSize: 12 }}>{day.source || "—"}</td>
                    <td>
                      {day.manual_status ? (
                        <StatusBadge status={day.manual_status} />
                      ) : (
                        <span className="text-muted" style={{ fontSize: 12 }}>N/A</span>
                      )}
                    </td>
                    <td style={{ fontSize: 12 }}>{day.first_in || "—"}</td>
                    <td style={{ fontSize: 12 }}>{day.last_out || "—"}</td>
                    <td style={{ fontSize: 11, color: "#555", maxWidth: 220 }}>{day.note || "—"}</td>
                    <td className="text-center">
                      <button
                        onClick={() => openManualModal(day.date)}
                        className="btn btn-xs btn-outline-primary"
                        style={{ fontSize: 11, padding: "2px 8px" }}
                      >
                        Mark
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>

          {/* Raw Biometric Logs (collapsible) */}
          <div className="mt-3">
            <button
              onClick={() => setShowRawLogs((v) => !v)}
              className="btn btn-outline-secondary btn-sm"
            >
              {showRawLogs ? "▲ Hide" : "▼ Show"} Raw Biometric Logs ({logsData.logs?.length || 0})
            </button>

            {showRawLogs && (
              <Table responsive bordered size="sm" className="mt-2">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Recorded At</th>
                    <th>Punch</th>
                    <th>Verification</th>
                  </tr>
                </thead>
                <tbody>
                  {!logsData.logs?.length ? (
                    <tr>
                      <td colSpan={4} className="text-center text-muted py-3">No raw logs.</td>
                    </tr>
                  ) : (
                    logsData.logs.map((log, i) => (
                      <tr key={log.id}>
                        <td>{i + 1}</td>
                        <td style={{ whiteSpace: "nowrap" }}>{log.recorded_at}</td>
                        <td>{log.punch_label}</td>
                        <td>{log.verification_label}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            )}
          </div>
        </>
      ) : (
        !loadingLogs && (
          <div className="text-center text-muted py-5">
            Enter an employee code and month, then click <b>Fetch Logs</b> or press <b>Enter</b>.
          </div>
        )
      )}

      {/* Manual Attendance Modal */}
      <Modal show={showManualModal} onHide={() => setShowManualModal(false)} centered size="sm">
        <Modal.Header className="modal_header_style" closeButton>
          <Modal.Title className="Modal_Heading_font">Mark Attendance</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            <label className="form-control-label"><b>Employee</b></label>
            <div style={{ padding: "6px 0", fontWeight: 500 }}>
              {logsData?.employee?.name} ({logsData?.employee?.emp_code})
            </div>
          </div>
          <div className="mb-3">
            <label className="form-control-label"><b>Date</b></label>
            <div style={{ padding: "6px 0", fontWeight: 500 }}>{manualDate}</div>
          </div>
          <div className="mb-3">
            <label className="form-control-label"><b>Status</b></label>
            <select
              className="form-control"
              value={manualStatus}
              onChange={(e) => setManualStatus(Number(e.target.value))}
            >
              {MANUAL_STATUS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          <div className="mb-1">
            <label className="form-control-label"><b>Session</b></label>
            <select
              className="form-control"
              value={selectedSession}
              onChange={(e) => setSelectedSession(e.target.value)}
            >
              <option value="">Auto (Active Session)</option>
              {sessions.map((s) => (
                <option key={s.id} value={s.id}>{s.year}</option>
              ))}
            </select>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button
            onClick={saveManualAttendance}
            disabled={savingManual}
            className="btn btn-success button_blue"
          >
            {savingManual ? <Spinner size="sm" animation="border" /> : "Save"}
          </button>
          <button onClick={() => setShowManualModal(false)} className="btn btn-danger btn_width">
            Cancel
          </button>
        </Modal.Footer>
      </Modal>
    </Paper>
  );
};

export default AttendanceLogs;
